import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';

import SuspenseLoader from 'src/components/SuspenseLoader';
import ProtectedRoute from './protected-route';

const Loader = (Component) => (props) =>
(
  <Suspense fallback={<SuspenseLoader />}>
    <Component {...props} />
  </Suspense>
);

// Pages

const Overview = Loader(lazy(() => import('src/content/overview')));

// Dashboards

const Home = Loader(lazy(() => import('src/content/dashboards/')));

// Master Data
const Login = Loader(
  lazy(() => import('src/layouts/AuthenticationLayout/Login'))
);

const ForgotPassword = Loader(
  lazy(() => import('src/layouts/AuthenticationLayout/Forgot'))
);

// Master Data
const Items = Loader(
  lazy(() => import('src/content/master_data/Item/'))
);
const Price = Loader(
  lazy(() => import('src/content/master_data/Price/'))
);
const Distributor = Loader(
  lazy(() => import('src/content/master_data/Distributor/'))
);
const Location = Loader(
  lazy(() => import('src/content/master_data/Location/'))
);
const ImportItem = Loader(
  lazy(() => import('src/content/master_data/Item/_partials/Import'))
);

// User Management

const Role = Loader(
  lazy(() => import('src/content/user_management/Role'))
);

const Manage = Loader(
  lazy(() => import('src/content/user_management/User'))
);

// Document
const Document = Loader(
  lazy(() => import('src/content/documents/'))
);
const Upload = Loader(
  lazy(() => import('src/content/documents/_partials'))
);
const Summary = Loader(
  lazy(() => import('src/content/documents/_partials/Summary'))
);

// Report
const Report = Loader(
  lazy(() => import('src/content/report'))
);
// Applications

const Messenger = Loader(
  lazy(() => import('src/content/applications/Messenger'))
);
const Transactions = Loader(
  lazy(() => import('src/content/applications/Transactions'))
);
const UserProfile = Loader(
  lazy(() => import('src/content/applications/Users/profile'))
);
const UserSettings = Loader(
  lazy(() => import('src/content/applications/Users/settings'))
);

// Status

const Status404 = Loader(
  lazy(() => import('src/content/pages/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Maintenance'))
);

const routes: RouteObject[] = [
  {
    path: '',
    element: <BaseLayout />,
    children: [
      {
        path: '/',
        element: <Login />
      },
      {
        path: 'login',
        element: <Login />
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />
      },
      {
        path: 'overview',
        element: <Navigate to="/" replace />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: 'dashboards',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="home" replace />
      },
      {
        path: 'home',
        element: <Home />
      },
      {
        path: 'messenger',
        element: <Messenger />
      }
    ]
  },
  {
    path: 'master',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="Master Data" replace />
      },
      {
        path: 'item',
        element: <Items />
      },
      {
        path: 'price',
        element: <Price />
      },
      {
        path: 'distributor',
        element: <Distributor />
      },
      {
        path: 'location',
        element: <Location />
      },
      {
        path: 'import-item',
        element: <ImportItem />
      },
    ]
  },
  {
    path: 'user',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="User Management" replace />
      },
      {
        path: 'role',
        element: <Role />
      },
      {
        path: 'manage',
        element: <Manage />
      },
    ]
  },
  {
    path: 'document',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="list" replace />
      },
      {
        path: 'list',
        element: <Document />
      },
      {
        path: 'upload',
        element: <Upload />
      },
      {
        path: 'summary',
        element: <Summary />
      },
    ]
  },
  {
    path: 'report',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="data" replace />
      },
      {
        path: 'data',
        element: <Report />
      },
    ]
  },
  {
    path: 'management',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'transactions',
        element: <Transactions />
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            element: <Navigate to="details" replace />
          },
          {
            path: 'details',
            element: <UserProfile />
          },
          {
            path: 'settings',
            element: <UserSettings />
          }
        ]
      }
    ]
  },

];

export default routes;
